import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { Logo } from '@/components/brand/Logo'
import { usePrefersReducedMotion } from '@/motion/use-reduced-motion'
import { brand, RULE } from '@/theme/tokens'

const TRACK_WIDTH = 160

/**
 * Espera com a cara do portal.
 *
 * O filete verde é o mesmo elemento que marca os títulos de seção; aqui ele
 * corre no lugar de ficar parado. Anima só `transform` (§7.7) e some com
 * `prefers-reduced-motion`, quando resta o filete parado e o texto — que é o
 * que de fato informa.
 *
 * O logo mantém o `view-transition-name` compartilhado, então ele continua o
 * mesmo elemento que vinha do card de login (M5) em vez de piscar.
 */
export function BrandLoader({ label = 'Carregando o painel…' }: { label?: string }) {
  const reduced = usePrefersReducedMotion()

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={label}
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        alignContent: 'center',
        gap: 3,
        bgcolor: 'background.default',
        px: 2,
      }}
    >
      <Logo size={40} />

      <Box
        aria-hidden
        sx={{
          width: TRACK_WIDTH,
          height: RULE.height,
          borderRadius: 0.5,
          overflow: 'hidden',
          bgcolor: 'divider',
          '@keyframes brand-loader-sweep': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(250%)' },
          },
        }}
      >
        <Box
          data-testid="brand-loader-bar"
          sx={{
            width: RULE.width,
            height: '100%',
            borderRadius: 0.5,
            bgcolor: brand.accent,
            ...(reduced
              ? { transform: 'translateX(0)' }
              : { animation: 'brand-loader-sweep 1.1s ease-in-out infinite' }),
          }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}
